import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import uPlot from 'uplot';
import { ApiLike, fetchSeries, isAbort } from './seriesClient';
import { PlotColumns, PlotData, PlotSpec, SeriesResponse } from './layoutTypes';

const MAX_FIELDS = 16;
const DEBOUNCE_MS = 150;

interface GroupState {
  resp?: SeriesResponse;
  error?: string;
  loading: boolean;
}

const groupKey = (topic: string, instance: number) => `${topic}|${instance}`;

/** Two points per pixel, in steps of 100 px so a resize does not refetch every frame. */
export const pointsFor = (plotWidth: number) =>
  Math.max(50, Math.min(4000, 2 * Math.ceil(Math.max(plotWidth, 100) / 100) * 100));

function friendlyError(err: any, topic: string): string {
  if (err?.status === 404) return `${topic} is not in this log, or a field was renamed.`;
  if (err?.status === 0 || err?.isNetworkError) return 'The analyst box did not answer.';
  return err?.message || 'The request failed.';
}

/**
 * The data for the visible tab's field plots: one request per topic and instance for the
 * current window, debounced, stale requests aborted. Plots that carry their own data are skipped.
 */
export function useTabData(
  api: ApiLike,
  logId: string,
  plots: PlotSpec[],
  window: [number, number] | null,
  plotWidth: number,
): { data: Record<string, PlotData>; retry: () => void } {
  const apiRef = useRef(api);
  apiRef.current = api;
  const [groups, setGroups] = useState<Record<string, GroupState>>({});
  const [nonce, setNonce] = useState(0);
  const retry = useCallback(() => setNonce(n => n + 1), []);

  // Which fields each topic must deliver, as a stable string so a re-render does not refetch.
  const needed = useMemo(() => {
    const m = new Map<string, { topic: string; instance: number; fields: Set<string> }>();
    plots.forEach(p => {
      if (p.inline) return;
      p.series.forEach(s => {
        const k = groupKey(s.topic, s.instance);
        const g = m.get(k) || { topic: s.topic, instance: s.instance, fields: new Set<string>() };
        g.fields.add(s.field);
        m.set(k, g);
      });
    });
    return Array.from(m.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, g]) => ({ key, topic: g.topic, instance: g.instance, fields: Array.from(g.fields).sort() }));
  }, [plots]);
  const neededSig = JSON.stringify(needed);
  const points = pointsFor(plotWidth);
  const windowSig = window ? `${window[0].toFixed(3)}:${window[1].toFixed(3)}` : '';

  // A different log shares nothing with this one.
  useEffect(() => { setGroups({}); }, [logId]);

  useEffect(() => {
    if (!logId || needed.length === 0) return undefined;
    setGroups(prev => {
      const next = { ...prev };
      needed.forEach(g => { next[g.key] = { ...(prev[g.key] || {}), loading: true, error: undefined }; });
      return next;
    });
    const controller = new AbortController();
    const timer = setTimeout(() => {
      needed.forEach(g => {
        const chunks: string[][] = [];
        for (let i = 0; i < g.fields.length; i += MAX_FIELDS) chunks.push(g.fields.slice(i, i + MAX_FIELDS));
        Promise.all(chunks.map(fields => fetchSeries(apiRef.current, logId, {
          topic: g.topic,
          instance: g.instance,
          fields,
          t0: window ? window[0] : undefined,
          t1: window ? window[1] : undefined,
          points,
        }, controller.signal)))
          .then(parts => {
            if (controller.signal.aborted) return;
            // Chunks of one topic share their time column: bucketing depends on the rows only.
            const merged: SeriesResponse = parts.length === 1
              ? parts[0]
              : { ...parts[0], fields: Object.assign({}, ...parts.map(p => p.fields)) };
            setGroups(prev => ({ ...prev, [g.key]: { resp: merged, loading: false } }));
          })
          .catch(err => {
            if (isAbort(err) || controller.signal.aborted) return;
            setGroups(prev => ({ ...prev, [g.key]: { ...(prev[g.key] || {}), loading: false, error: friendlyError(err, g.topic) } }));
          });
      });
    }, DEBOUNCE_MS);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [logId, neededSig, windowSig, points, nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per plot, reuse the previous object when its inputs did not move, so memoised plots stay put.
  const cache = useRef(new Map<string, { inputs: unknown[]; data: PlotData }>());
  const data = useMemo(() => {
    const out: Record<string, PlotData> = {};
    const seen = new Set<string>();
    plots.forEach(p => {
      if (p.inline || p.series.length === 0) return;
      seen.add(p.id);
      const states = p.series.map(s => groups[groupKey(s.topic, s.instance)]);
      const inputs: unknown[] = [p.series, ...states.map(st => st?.resp), ...states.map(st => st?.error)];
      const prev = cache.current.get(p.id);
      if (prev && prev.inputs.length === inputs.length && prev.inputs.every((v, i) => v === inputs[i])) {
        out[p.id] = prev.data;
        return;
      }
      const next = assemble(p, states);
      cache.current.set(p.id, { inputs, data: next });
      out[p.id] = next;
    });
    Array.from(cache.current.keys()).forEach(k => { if (!seen.has(k)) cache.current.delete(k); });
    return out;
  }, [plots, groups]);

  return { data, retry };
}

function assemble(plot: PlotSpec, states: (GroupState | undefined)[]): PlotData {
  const failed = states.find(s => s?.error && !s.resp);
  if (failed) return { status: 'error', error: failed.error };
  if (states.some(s => !s?.resp)) return { status: 'loading' };

  const resps = states.map(s => s!.resp!);
  const bucketed = resps.some(r => r.bucketed);
  const distinct = Array.from(new Set(resps));
  if (distinct.length === 1) {
    const r = distinct[0];
    const nulls = () => r.t.map(() => null);
    const columns = [r.t, ...plot.series.map(s => r.fields[s.field] || nulls())] as PlotColumns;
    return { status: 'ready', columns, bucketed };
  }

  // Several topics: one table each, outer-joined on time, then put back in the plot's order.
  const tables: { resp: SeriesResponse; fields: string[] }[] = distinct.map(resp => ({ resp, fields: [] }));
  const where = plot.series.map((s, i) => {
    const ti = distinct.indexOf(resps[i]);
    const tbl = tables[ti];
    let ci = tbl.fields.indexOf(s.field);
    if (ci < 0) { tbl.fields.push(s.field); ci = tbl.fields.length - 1; }
    return { ti, ci };
  });
  const joined = uPlot.join(tables.map(t => [
    t.resp.t,
    ...t.fields.map(f => t.resp.fields[f] || t.resp.t.map(() => null)),
  ]) as uPlot.AlignedData[]);
  const offsets: number[] = [];
  tables.reduce((acc, t, i) => { offsets[i] = acc; return acc + t.fields.length; }, 1);
  const columns = [
    joined[0] as number[],
    ...where.map(w => joined[offsets[w.ti] + w.ci] as (number | null | undefined)[]),
  ] as PlotColumns;
  return { status: 'ready', columns, bucketed };
}
